/**
 * Чистая половина списка мест раздела «Баркод»: как место ЗОВЁТСЯ в строке,
 * как список сужается городом и чем подписана поставка, в которую место уехало.
 *
 * Здесь нет ни React, ни DOM. Имя короба, набор городов и подпись поставки —
 * это функции над строками ответа, и вынесены они ровно затем, чтобы их
 * проверял тест, а не глаз на живом экране: DOM-тестов в проекте нет.
 */

import { day } from "./format";

/** То, что нужно знать о месте, чтобы его назвать и отнести к городу. */
export type PlaceLike = {
  place_id: number;
  name: string | null;
  /** Номер короба на складе (`sPlace.sortOrder`). У прочих мест смысла не несёт. */
  code: string;
  korob: boolean;
  city_id: number | null;
  city: string | null;
  /** Место города в справочнике CRM (`sPlace_locationID.sortOrder`). */
  city_order: number | null;
  /** Сколько вещей этого размера лежит здесь сейчас. У мест истории — ноль. */
  units: number;
};

/**
 * Как место зовётся в строке списка.
 *
 * У КОРОБА это «Короб 10944» — номер, и только он (замечание владельца
 * 09.09.26). Дело не в одной лишней подписи: имя короба в CRM городу НЕ РАВНО
 * и часто ему противоречит. Настоящее имя места 38011 — «Невиномыск Невиномыск
 * Короб 10944», и таких коробов, где город стоит в имени дважды, 4 246 из
 * 12 609 в группе 218; при этом в справочнике локаций у них Ростов и Воронеж
 * (замер `crm-prod` 09.09.26). Строка показывала «Невиномыск · Невиномыск
 * Невиномыск Короб 10944 · короб 10944» — город трижды, из них дважды чужой, и
 * номер дважды.
 *
 * Поэтому номер берётся из `sortOrder`, а город — из справочника: два поля,
 * каждое из которых знает свой ответ, вместо имени, которое не знает ни того,
 * ни другого.
 *
 * У прочих мест берётся имя как есть: «РНД Списано», «Химчистка» — там имя и
 * есть всё, что о месте известно, и вырезать из него город догадкой значило бы
 * ломать те названия, где город — часть смысла.
 *
 * Зовёт правило и ЛЕНТА ПЕРЕМЕЩЕНИЙ (10.09.26): раньше оба конца печатали имя
 * из CRM целиком, и «Невиномыск Невиномыск Короб 10988» занимало в колонке три
 * строки, повторяя город, который тут же стоит подписью. С тех пор как концы
 * перемещения стали ссылками, это ещё и адрес — а адрес обязан читаться.
 *
 * Принимает ЧАСТЬ `PlaceLike`: имя места складывается из четырёх полей, а
 * города и остатка у конца перемещения нет вовсе.
 */
export function placeLabel(
  place: Pick<PlaceLike, "place_id" | "name" | "code" | "korob">,
): string {
  const code = place.code.trim();
  if (place.korob && code) return `Короб ${code}`;
  return place.name || `Место ${place.place_id}`;
}

/** Город в отборе: чем он зовётся, сколько за ним мест и вещей. */
export type CityOption = {
  /** Значение отбора строкой — как в адресе и в `Combo`. */
  value: string;
  label: string;
  places: number;
  /**
   * Вещей этого размера в городе. У списка «где лежал» он ноль у всех: там
   * ничего не лежит, и городá меряются местами.
   */
  units: number;
  /** Место города в справочнике CRM — им и задан порядок кнопок. */
  order: number;
};

/**
 * Куда встаёт город, которому в справочнике порядка не досталось.
 *
 * В хвост, но ПЕРЕД «без города»: место с неведомым порядком — всё-таки город,
 * а строка без города — остаток. Число заведомо больше живых значений
 * справочника (там 46 строк, максимум 30-х).
 */
const LAST = Number.MAX_SAFE_INTEGER;

/**
 * Место без города. Такое бывает: справочник локаций подцеплен `LEFT JOIN`, и
 * у давно удалённого места города может не остаться вовсе.
 *
 * Своё значение отбора, а не молчаливый пропуск: сумма по городам обязана
 * сходиться со списком, иначе отбор «все» и сумма его городов расходятся, и
 * непонятно, где потерялись строки.
 */
export const NO_CITY = "0";

/**
 * Города списка мест — В ПОРЯДКЕ СПРАВОЧНИКА CRM, по возрастанию
 * `sPlace_locationID.sortOrder` (требование владельца 09.09.26).
 *
 * Считаются ИЗ САМИХ СТРОК, а не отдельным запросом: города уже приехали в
 * каждой строке ответа, и второй источник этого же множества расходился бы с
 * первым ровно тогда, когда список чем-нибудь сужен.
 *
 * Порядок ЗАВЕДЁН В CRM и означает не «где больше вещей», а как склад привык
 * перечислять города: Москва 1, Петербург 2, Краснодар 3, Ростов 14, Воронеж
 * 24, Самара 25. Так кнопки стоят на месте, куда за ними тянется рука, — а
 * порядок «где больше, то выше», стоявший здесь до правки, переставлял их от
 * товара к товару и от одного отбора к другому.
 *
 * `sortOrder` в справочнике НЕ уникален (у 30 сидит десяток городов), поэтому
 * при равенстве решает имя: без второго ключа порядок кнопок зависел бы от
 * того, в каком порядке пришли строки мест.
 */
export function cityOptions(rows: readonly PlaceLike[]): CityOption[] {
  const seen = new Map<string, CityOption>();
  for (const row of rows) {
    const value = row.city_id ? String(row.city_id) : NO_CITY;
    const got = seen.get(value);
    if (got) {
      got.places += 1;
      got.units += row.units;
      continue;
    }
    seen.set(value, {
      value,
      label: (row.city || "").trim() || "без города",
      places: 1,
      units: row.units,
      order: row.city_order ?? LAST,
    });
  }
  // «Без города» всегда последним, каким бы длинным он ни был: это не город, а
  // остаток, и в середине списка городов он читался бы как один из них.
  return [...seen.values()].sort(
    (a, b) =>
      Number(a.value === NO_CITY) - Number(b.value === NO_CITY) ||
      a.order - b.order ||
      a.label.localeCompare(b.label, "ru"),
  );
}

/**
 * Строки одного города. Пустой отбор — весь список, и это не то же самое, что
 * «город не выбран, значит ничего не показывать».
 */
export function byCity<T extends PlaceLike>(
  rows: readonly T[],
  city: string,
): T[] {
  if (!city) return [...rows];
  return rows.filter((row) => (row.city_id ? String(row.city_id) : NO_CITY) === city);
}

/**
 * Поставка места так, как её читает человек в тесной ячейке таблицы.
 *
 * Задача владельца 11.09.26: по коробу видно, КУДА УШЁЛ ТОВАР — на какую
 * площадку, каким документом, на какой склад и когда. Ячейка узкая, а полей у
 * поставки восемь, поэтому строк две и они отвечают на разные вопросы:
 *
 *     head   чем поставку зовут   «ВБ · WB-GI-273619491»
 *     tail   куда и когда уехала  «Невинномысск · 05.09.2026»
 *
 * Остальное — в подсказке: статус, второй номер поставки (у ВБ он свой,
 * `mpSupplyOrderCode`) и обе даты порознь. Так строка остаётся читаемой на
 * ходу, а полный ответ никуда не пропадает.
 *
 * ДАТА В `tail` — ФАКТ ОТГРУЗКИ, если он известен, и план, если нет. У Ozon
 * факта не бывает вовсе, и подпись поэтому едет вместе с датой («отгружено» /
 * «план»): дата без имени читалась бы как факт на обеих площадках, а половина
 * из них — намерение.
 */
export type SupplyLike = {
  id: number;
  platform_name: string | null;
  code: string | null;
  number: number | null;
  status: string | null;
  date: string | null;
  date_fact: string | null;
  warehouse: string | null;
  region: string | null;
};

export type SupplyView = { head: string; tail: string; title: string };

export function supplyView(supply: SupplyLike): SupplyView {
  // Номер поставки в CRM — последнее имя, которым её можно назвать: без кода и
  // без номера остаётся внутренний `mpSupplyID`, и он лучше пустой ячейки —
  // по нему поставку хотя бы найдут в базе.
  const name = supply.code || (supply.number ? String(supply.number) : `№${supply.id}`);
  const head = [supply.platform_name, name].filter(Boolean).join(" · ");

  const fact = Boolean(supply.date_fact);
  const at = day(supply.date_fact || supply.date);
  const when = at ? `${fact ? "отгружено" : "план"} ${at}` : "";
  // Склад сдачи и кластер назначения при кроссдоке РАЗНЫЕ, и стрелка между
  // ними — весь смысл пары: «сдали в Воронеже, поедет в Краснодар».
  const where = [supply.warehouse, supply.region].filter(Boolean).join(" → ");
  const tail = [where, when].filter(Boolean).join(" · ");

  const title = [
    `Поставка ${name}`,
    supply.platform_name,
    supply.status,
    // Второе имя ВБ-поставки показывается, только если оно ДРУГОЕ: у Ozon его
    // нет вовсе, а у ВБ без кода `WB-GI-…` оно уже стоит в самой строке.
    supply.number && String(supply.number) !== name ? `номер в CRM ${supply.number}` : "",
    supply.warehouse ? `склад ${supply.warehouse}` : "",
    supply.region ? `направление ${supply.region}` : "",
    supply.date ? `план ${day(supply.date)}` : "",
    fact ? `отгружено ${day(supply.date_fact)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return { head, tail, title };
}
